import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

/**
 * The tenant's own Graviton Pay (paymentsv3) credentials.
 *
 * Per tenant, not per platform: each lender settles into its own paymentsv3
 * account, so each supplies its own client_id/profile_id. A lender with no
 * row is simply not onboarded, and LIVE mode refuses rather than falling
 * back to anyone else's.
 */

/**
 * Never contains the module key — the backend DTO has no field capable of
 * carrying it, and `config` has it stripped. `hasSecret` says only whether
 * one is stored.
 */
export interface PaymentGatewayCredentialMetadata {
  configured: boolean
  hasSecret: boolean
  config: Record<string, unknown>
  updatedAt: string | null
  updatedBy: string | null
}

/**
 * The readable half. module_key is NOT here: it authenticates every call, so
 * it is stored encrypted and never sent back — see `secret` below.
 */
export interface PaymentGatewayConfig {
  go_base_url: string
  base_url: string
  client_id: string
  profile_id: string
  environment: string
}

export interface PaymentGatewayCredentialSave {
  config: PaymentGatewayConfig
  /** The module key. Omitted or blank keeps the stored one. */
  secret?: string
}

const KEY = ['settings', 'payment-gateway', 'credentials']

export function usePaymentGatewayCredential() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await api.get<PaymentGatewayCredentialMetadata>('/v1/payments/gateway/credentials')).data,
  })
}

export function useSavePaymentGatewayCredential() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: PaymentGatewayCredentialSave) => {
      await api.put('/v1/payments/gateway/credentials', body)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
